import React from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import Button from '../../../components/atoms/forms/Button';
import { driverRecordShape } from './driverRecordShape';
import {
  SORT_ACTUAL,
  SORT_MODES,
  SORT_SCHEDULED,
  orderStops,
  stopDisplayNumbers,
} from './stopOrdering';
import {
  STOP_STATE_LABEL,
  STOP_STATE_TONE,
  discClassVariants,
  toneClassVariants,
} from './statusStyles';
import {
  stopDisplayState,
  stripStopSuffix,
} from '../../../utils/composeDriverRecords';
import {
  formatDeltaLabel,
  formatDriverNames,
  formatDurationMinutes,
  formatMiles,
  formatOnSiteDuration,
  pluralize,
} from '../../../utils/displayFormat';
import { trailDistanceMiles } from './trailGeometry';

// The Single-route map's stop timeline: a route summary, a sort pill that
// flips between the planned order and the order the driver actually worked,
// and one row per stop with its arrival/departure story. The disc numbers
// here are the SAME numbers the map draws (stopOrdering keeps them in
// lockstep), so reading across panel and map never needs translation.

// Row chip tones: the stop display states, plus 'late' for a stocked stop
// that came in behind its planned window.
const CHIP_TONE = {
  ...STOP_STATE_TONE,
  late: { bg: theme.colors.statusBehindBg, fg: theme.colors.statusBehind },
};

const SORT_LABEL = {
  [SORT_SCHEDULED]: 'Sort by scheduled',
  [SORT_ACTUAL]: 'Sort by actual',
};
const SORT_HINT = {
  [SORT_SCHEDULED]:
    'Showing the planned schedule. Click to sort by the order the driver actually worked.',
  [SORT_ACTUAL]:
    'Showing the order the driver actually worked. Click to sort by the planned schedule.',
};

const StyledDiv = styledComponent('div', {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,

  '& .rsp-facts': {
    display: 'flex',
    flexDirection: 'column',
    rowGap: theme.spacing.gapXs,
    padding: `0 ${theme.spacing.gapMd}`,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textBody,
  },
  '& .rsp-fact-muted': {
    color: theme.colors.textMuted,
  },
  '& .rsp-fact-late': {
    color: theme.colors.statusOffTrack,
    fontWeight: theme.fontWeights.medium,
  },

  '& .rsp-sort-row': {
    margin: `${theme.spacing.gapXl} 0`,
    padding: `${theme.spacing.gapMd} 0 0`,
    display: 'flex',
    justifyContent: 'center',
    borderTop: `1px solid ${theme.colors.borderLight}`,
  },

  '& .rsp-list': {
    listStyle: 'none',
    margin: 0,
    padding: `0 ${theme.spacing.gapMd}`,
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  '& .rsp-row': {
    display: 'grid',
    gridTemplateColumns: `${theme.sizes.stopBadgeSize} 1fr auto`,
    columnGap: theme.spacing.gapLg,
    alignItems: 'start',
    padding: `${theme.spacing.gapLg} 0`,
  },
  '& .rsp-row + .rsp-row': {
    borderTop: `1px solid ${theme.colors.borderLight}`,
  },
  // The same disc the map draws for this stop (STOP_DISC_FILL).
  '& .rsp-disc': {
    width: theme.sizes.stopBadgeSize,
    height: theme.sizes.stopBadgeSize,
    borderRadius: theme.radii.circle,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    ...theme.typography.badge,
    fontWeight: theme.fontWeights.semibold,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
  },
  ...discClassVariants('rsp-disc'),
  '& .rsp-stop-name': {
    fontWeight: theme.fontWeights.semibold,
    overflowWrap: 'anywhere',
  },
  '& .rsp-stop-lines': {
    marginTop: theme.spacing.gapXs,
    display: 'flex',
    flexDirection: 'column',
    rowGap: theme.spacing.gapXs,
    fontSize: theme.sizes.captionFontSize,
    color: theme.colors.textBody,
    fontVariantNumeric: 'tabular-nums',
  },
  '& .rsp-late': {
    color: theme.colors.statusOffTrack,
  },
  '& .rsp-line-muted': {
    color: theme.colors.textMuted,
  },
  '& .rsp-chip': {
    padding: theme.spacing.pillPadding,
    borderRadius: theme.radii.pill,
    fontSize: theme.sizes.captionFontSize,
    fontWeight: theme.fontWeights.medium,
    whiteSpace: 'nowrap',
  },
  ...toneClassVariants('rsp-chip', CHIP_TONE),
  '& .rsp-empty': {
    padding: theme.spacing.cardPadding,
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    textAlign: 'center',
  },
});

// One chip per row: what happened at (or is owed to) this stop.
function chipFor(stop) {
  const displayState = stopDisplayState(stop);
  if (displayState === 'done' && stop.delta > 0) {
    return { tone: 'late', label: `${STOP_STATE_LABEL.done} late` };
  }
  return { tone: displayState, label: STOP_STATE_LABEL[displayState] };
}

// The story lines for one stop, given what it reported: what to expect (and
// what was planned) while it is still ahead or in flight; arrival, departure,
// time on site and who stocked it once done.
function StopLines({ stop }) {
  if (stop.state !== 'done') {
    // A skipped stop shows what was planned, not a forecast ETA — the route
    // has moved past it, so any projection would be a guess. A stop the
    // driver has already arrived at shows the arrival instead of an ETA.
    const eta = stop.skipped ? null : stop.currentEtaLabel || stop.etaLabel;
    return (
      <div className="rsp-stop-lines">
        {stop.scannedAt ? <span>{`Scanned ${stop.scannedAt}`}</span> : null}
        {eta && !stop.scannedAt ? <span>{`ETA ${eta}`}</span> : null}
        {stop.plannedWindow ? (
          <span className="rsp-line-muted">{`Planned ${stop.plannedWindow}`}</span>
        ) : null}
      </div>
    );
  }
  const lateNote =
    stop.delta > 0 ? (
      <span className="rsp-late">{` (${formatDurationMinutes(
        stop.delta,
      )} late)`}</span>
    ) : null;
  const hasDuration =
    stop.actualDurationMin !== null && stop.actualDurationMin !== undefined;
  return (
    <div className="rsp-stop-lines">
      {stop.scannedAt ? (
        <span>
          {`Scanned ${stop.scannedAt}`}
          {lateNote}
        </span>
      ) : null}
      {stop.stockedAt ? (
        <span>
          {`Stocked ${stop.stockedAt}`}
          {stop.scannedAt ? null : lateNote}
        </span>
      ) : null}
      {hasDuration ? (
        <span className="rsp-line-muted">
          {formatOnSiteDuration(stop.actualDurationMin, stop.type)}
        </span>
      ) : null}
      {stop.stockedByName ? (
        <span className="rsp-line-muted">
          {`Stocked by ${stop.stockedByName}`}
        </span>
      ) : null}
    </div>
  );
}

StopLines.propTypes = {
  stop: PropTypes.shape({
    state: PropTypes.string,
    skipped: PropTypes.bool,
    // OpsLocations location type ('FRIDGE', …) — names the on-site duration.
    type: PropTypes.string,
    scannedAt: PropTypes.string,
    stockedAt: PropTypes.string,
    actualDurationMin: PropTypes.number,
    delta: PropTypes.number,
    etaLabel: PropTypes.string,
    currentEtaLabel: PropTypes.string,
    plannedWindow: PropTypes.string,
    stockedByName: PropTypes.string,
  }).isRequired,
};

function RouteStopsPanel({ driver, sortMode, onSortModeChange, onSelectStop }) {
  const stops = driver.routeStops || [];
  const orderedStops = orderStops(stops, sortMode);
  const numbers = stopDisplayNumbers(stops, sortMode);
  const milesDriven = formatMiles(trailDistanceMiles(driver.pingTrail || []));
  const skippedCount = stops.filter((stop) => stop.skipped).length;
  const nextSortMode = sortMode === SORT_ACTUAL ? SORT_SCHEDULED : SORT_ACTUAL;

  return (
    <StyledDiv>
      <div className="rsp-facts">
        <span>{formatDriverNames(driver.driverNames)}</span>
        {milesDriven ? (
          <span className="rsp-fact-muted">{`${milesDriven} traveled`}</span>
        ) : null}
        <span>
          {`${driver.stopsCompleted} / ${driver.stopsTotal} completed`}
          {skippedCount > 0 ? (
            <span className="rsp-fact-late">
              {` · ${pluralize(skippedCount, 'skipped stop')}`}
            </span>
          ) : null}
          {driver.etaDeltaMin > 0 ? (
            <span className="rsp-fact-late">
              {` · ${formatDeltaLabel(driver.etaDeltaMin, { unit: 'min' })}`}
            </span>
          ) : null}
        </span>
      </div>
      <div className="rsp-sort-row">
        <Button
          variant="secondary"
          onClick={() => onSortModeChange(nextSortMode)}
          title={SORT_HINT[sortMode]}
          dataTestId="rsp-sort-toggle"
        >
          {SORT_LABEL[sortMode]}
        </Button>
      </div>
      {orderedStops.length === 0 ? (
        <div className="rsp-empty">No stops reported for this route yet.</div>
      ) : (
        <ol className="rsp-list">
          {orderedStops.map((stop) => {
            const chip = chipFor(stop);
            return (
              <li key={stop.stopId} className="rsp-row">
                <span className={`rsp-disc ${stopDisplayState(stop)}`}>
                  {numbers.get(stop.stopId)}
                </span>
                <div>
                  <div className="rsp-stop-name">
                    <Button
                      variant="link-primary"
                      title={`Show ${stop.stopName} on the map`}
                      onClick={() => onSelectStop(stop)}
                    >
                      {stripStopSuffix(stop.stopId) || stop.stopName}
                    </Button>
                  </div>
                  <StopLines stop={stop} />
                </div>
                <span className={`rsp-chip ${chip.tone}`}>{chip.label}</span>
              </li>
            );
          })}
        </ol>
      )}
    </StyledDiv>
  );
}

RouteStopsPanel.propTypes = {
  driver: driverRecordShape.isRequired,
  sortMode: PropTypes.oneOf(SORT_MODES).isRequired,
  onSortModeChange: PropTypes.func.isRequired,
  // Receives the clicked stop; the pane pans the map to it.
  onSelectStop: PropTypes.func.isRequired,
};

export default RouteStopsPanel;
